import styled from 'styled-components';

import SkeletonBox from './SkeletonBox';

/**
 * Skeleton of a text document that is still being written: an optional
 * heading line, then paragraphs of lines whose widths vary the way prose
 * does. The shape every generated text product (article, social post,
 * email body, talking points) shares, composed from `SkeletonBox` — see
 * "Loading & Progress" in `docs/design-system.md`.
 */

/* Line widths cycle so a paragraph reads as ragged prose, ending short. */
const LINE_WIDTHS = ['100%', '94%', '97%', '88%'];

type ParagraphProps = {
  lines?: number;
  lineHeight?: string;
};

export function SkeletonParagraph({
  lines = 4,
  lineHeight = '12px',
}: ParagraphProps) {
  return (
    <Paragraph>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonBox
          key={i}
          $height={lineHeight}
          $width={i === lines - 1 ? '62%' : LINE_WIDTHS[i % LINE_WIDTHS.length]}
        />
      ))}
    </Paragraph>
  );
}

type DocumentProps = {
  /** Heading line at the top; `false` for a body-only document. */
  heading?: boolean;
  paragraphs?: number;
};

export function SkeletonDocument({
  heading = true,
  paragraphs = 4,
}: DocumentProps) {
  return (
    <Document aria-hidden>
      {heading && <SkeletonBox $width="70%" $height="22px" $radius="6px" />}
      {Array.from({ length: paragraphs }, (_, i) => (
        <SkeletonParagraph key={i} lines={i % 2 === 0 ? 5 : 3} />
      ))}
    </Document>
  );
}

const Document = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Paragraph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
